import { useState, useEffect } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { supabase } from '../lib/supabase';

export const useCapsuleFavorites = () => {
  const { isConnected, publicKey } = useWallet();
  const [favoriteCapsules, setFavoriteCapsules] = useState(new Set());

  // Load favorites from Supabase or localStorage
  const loadFavorites = async () => {
    const userWallet = window.localStorage.getItem('connectedWallet');
    
    if (userWallet && isConnected) {
      // Load from Supabase when connected
      try {
        console.log('🔄 useCapsuleFavorites: Loading from Supabase for wallet:', userWallet);
        const { data, error } = await supabase
          .from('favorites')
          .select('item_id')
          .eq('user_wallet', userWallet.toLowerCase())
          .eq('item_type', 'capsule');

        if (error) {
          console.error('Error loading capsule favorites from Supabase:', error);
        } else {
          const ids = (data || []).map(fav => fav.item_id);
          console.log('✅ useCapsuleFavorites: Loaded capsule IDs:', ids);
          setFavoriteCapsules(new Set(ids));
          return;
        }
      } catch (error) {
        console.error('Error loading from Supabase:', error);
      }
    }
    
    // Fallback to localStorage
    const savedFavorites = localStorage.getItem('favoriteCapsules');
    if (savedFavorites) {
      try {
        const favoritesArray = JSON.parse(savedFavorites);
        // Extract just the IDs from the full capsule data
        const ids = favoritesArray.map(fav => typeof fav === 'string' ? fav : fav.id);
        setFavoriteCapsules(new Set(ids));
      } catch (error) {
        console.error('Error loading capsule favorites:', error);
      }
    }
  };

  // Load favorites on mount and when wallet changes
  useEffect(() => {
    loadFavorites();
  }, [isConnected, publicKey]);

  // Listen for storage events to sync across tabs/components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'capsuleFavoritesChanged') {
        console.log('🔄 useCapsuleFavorites: Favorites changed, reloading...');
        loadFavorites();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom events on the same page
    window.addEventListener('capsuleFavoritesChanged', loadFavorites);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('capsuleFavoritesChanged', loadFavorites);
    };
  }, [isConnected, publicKey]);

  // Add capsule to favorites with full data
  const addCapsuleToFavorites = (capsule) => {
    try {
      const existingFavorites = JSON.parse(localStorage.getItem('favoriteCapsules') || '[]');
      const favoriteCapsule = {
        id: capsule.id,
        title: capsule.title || capsule.name,
        name: capsule.name || capsule.title,
        description: capsule.description || '',
        banner: capsule.banner || capsule.image || '',
        image: capsule.image || capsule.banner || '',
        creator: capsule.creator || capsule.creator_handle || '',
        pieceCount: capsule.pieceCount || capsule.pieces || 0,
        avgPrice: capsule.avgPrice || capsule.price || 0,
        price: capsule.price || capsule.avgPrice || 0,
        dropDate: capsule.dropDate || capsule.createdAt || capsule.created_at || new Date().toISOString(),
        createdAt: capsule.createdAt || capsule.created_at || capsule.dropDate || new Date().toISOString(),
        featured: capsule.featured,
        capsuleNumber: capsule.capsuleNumber || '',
        addedAt: new Date().toISOString()
      };
      
      // Check if already exists
      const exists = existingFavorites.some(fav => fav.id === capsule.id);
      if (!exists) {
        existingFavorites.push(favoriteCapsule);
        localStorage.setItem('favoriteCapsules', JSON.stringify(existingFavorites));
      }
    } catch (error) {
      console.error('Error adding capsule to favorites:', error);
    }
  };

  // Remove capsule from favorites
  const removeCapsuleFromFavorites = (capsuleId) => {
    try {
      const existingFavorites = JSON.parse(localStorage.getItem('favoriteCapsules') || '[]');
      const updatedFavorites = existingFavorites.filter(fav => fav.id !== capsuleId);
      localStorage.setItem('favoriteCapsules', JSON.stringify(updatedFavorites));
    } catch (error) {
      console.error('Error removing capsule from favorites:', error);
    }
  };

  // Toggle favorite status for capsules
  const toggleCapsuleFavorite = async (capsule) => {
    if (!isConnected) {
      console.warn('Please connect your wallet to favorite capsules');
      return false;
    }

    // Handle both capsule object and capsule ID
    const capsuleId = typeof capsule === 'string' ? capsule : capsule.id;
    const isCurrentlyFavorited = favoriteCapsules.has(capsuleId);

    console.log('🔄 toggleCapsuleFavorite called:', { capsuleId, isCurrentlyFavorited, capsule });

    try {
      // Get wallet address from context - need to import useWallet at component level
      const userWallet = window.localStorage.getItem('connectedWallet');
      
      console.log('💼 Connected wallet:', userWallet);
      
      if (userWallet) {
        // Use Supabase when wallet is available
        if (isCurrentlyFavorited) {
          // Remove from Supabase
          console.log('🗑️ Removing capsule from favorites...');
          const { error } = await supabase
            .from('favorites')
            .delete()
            .eq('user_wallet', userWallet.toLowerCase())
            .eq('item_type', 'capsule')
            .eq('item_id', capsuleId);

          if (error) {
            console.error('❌ Error removing capsule from Supabase:', error);
          } else {
            console.log('✅ Successfully removed capsule from Supabase');
          }
        } else {
          // Add to Supabase with metadata
          const metadata = typeof capsule === 'object' ? {
            title: capsule.title || capsule.name,
            name: capsule.name || capsule.title,
            description: capsule.description || '',
            banner: capsule.banner || capsule.image || '',
            image: capsule.image || capsule.banner || '',
            creator: capsule.creator || capsule.creator_handle || '',
            pieceCount: capsule.pieceCount || capsule.pieces || 0,
            avgPrice: capsule.avgPrice || capsule.price || 0,
            price: capsule.price || capsule.avgPrice || 0,
            dropDate: capsule.dropDate || capsule.createdAt || capsule.created_at || new Date().toISOString(),
            createdAt: capsule.createdAt || capsule.created_at || capsule.dropDate || new Date().toISOString(),
            capsuleNumber: capsule.capsuleNumber || '',
            addedAt: new Date().toISOString()
          } : {};

          console.log('➕ Adding capsule to favorites with metadata:', metadata);

          const { error, data } = await supabase
            .from('favorites')
            .insert({
              user_wallet: userWallet.toLowerCase(),
              item_type: 'capsule',
              item_id: capsuleId,
              metadata: metadata
            })
            .select();

          if (error) {
            // Handle duplicate key error - item is already favorited
            if (error.code === '23505') {
              console.log('⚠️ Capsule already favorited in database, updating local state');
              // Still update local state even if duplicate
            } else {
              console.error('❌ Error adding capsule to Supabase:', error);
            }
          } else {
            console.log('✅ Successfully added capsule to Supabase:', data);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error with Supabase operation:', error);
    }

    // Also update localStorage for backward compatibility
    setFavoriteCapsules(prev => {
      const newFavorites = new Set(prev);
      if (isCurrentlyFavorited) {
        newFavorites.delete(capsuleId);
        // Remove from localStorage
        removeCapsuleFromFavorites(capsuleId);
      } else {
        newFavorites.add(capsuleId);
        // Add to localStorage with full data (only if capsule object is provided)
        if (typeof capsule === 'object') {
          addCapsuleToFavorites(capsule);
        }
      }
      
      return newFavorites;
    });
    
    // Notify all components that favorites changed
    window.dispatchEvent(new Event('capsuleFavoritesChanged'));
    console.log('📢 Dispatched capsuleFavoritesChanged event');
    
    return true;
  };

  // Check if a capsule is favorited
  const isCapsuleFavorited = (capsuleId) => {
    return favoriteCapsules.has(capsuleId);
  };

  // Get all favorite capsule IDs
  const getFavoriteCapsuleIds = () => {
    return [...favoriteCapsules];
  };

  return {
    favoriteCapsules,
    toggleCapsuleFavorite,
    isCapsuleFavorited,
    getFavoriteCapsuleIds,
    isConnected
  };
};
